import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveEquipmentsAddonsFieldErrors,
  reserveEquipmentsAddonsFieldHelperText,
} from "../../../constants/reserveEquipmentsAddonsValidationHelperText";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS,
  RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY,
  RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION,
  RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME,
  RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY,
  RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_TYPE,
  RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE,
  RESERVE_EQUIPMENTS_ADDON_FIELD_PRICING_TYPE,
  RESERVE_EQUIPMENTS_ADDON_PRICING_TYPE_OPTIONS,
  RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK,
  RESERVE_EQUIPMENTS_ADDON_LIST_CODE_KEY,
  RESERVE_EQUIPMENTS_ADDON_LIST_NAME_KEY,
} from "../../../constants/ReserveEquipmentsAddOnsFields";
import {
  reserveEquipmentsAddonsTooltipAdditionalRatesPerMins,
  reserveEquipmentsAddonsTooltipAddOnCategory,
  reserveEquipmentsAddonsTooltipAddOnDescription,
  reserveEquipmentsAddonsTooltipAddOnName,
  reserveEquipmentsAddonsTooltipAddOnSubCategory,
  reserveEquipmentsAddonsTooltipAddOnType,
  reserveEquipmentsAddonsTooltipHeader,
  reserveEquipmentsAddonsTooltipPrice,
  reserveEquipmentsAddonsTooltipPricingType,
  reserveEquipmentsAddonsTooltipQtyInStock,
  reserveListAvailableHeadingTooltip,
} from "../../../constants/ReserveTooltipInformation";

const ReserveEquipmentsAddOns = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [addOnName, setAddOnName] = useState<string | null>(null);
  const [addOnType, setAddOnType] = useState<string | null>(null);
  const [addOnDescription, setAddOnDescription] = useState<string | null>(null);
  const [addOnCategory, setAddOnCategory] = useState<string | null>(null);
  const [addOnSubCategory, setAddOnSubCategory] = useState<string | null>(null);
  const [qtyInStock, setQtyInStock] = useState<string | null>(null);
  const [pricingType, setPricingType] = useState<string | null>(null);
  const [price, setPrice] = useState<string | null>(null);
  const [additionalRatesPerMins, setAdditionalRatesPerMins] = useState<
    string | null
  >(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [addOnTypeErr, setAddOnTypeErr] = useState<boolean>(false);
  const [addOnDescriptionErr, setAddOnDescriptionErr] =
    useState<boolean>(false);
  const [addOnCategoryErr, setAddOnCategoryErr] = useState<boolean>(false);
  const [addOnSubCategoryErr, setAddOnSubCategoryErr] =
    useState<boolean>(false);
  const [qtyInStockErr, setQtyInStockErr] = useState<boolean>(false);
  const [priceErr, setPriceErr] = useState<boolean>(false);
  const [additionalRatesPerMinsErr, setAdditionalRatesPerMinsErr] =
    useState<boolean>(false);
  const [addOnInformation, setAddOnInformation] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateInput = () => {
    setNameError(false);
    setAddOnTypeErr(false);
    setAddOnDescriptionErr(false);
    setAddOnCategoryErr(false);
    setAddOnSubCategoryErr(false);
    setQtyInStockErr(false);
    setPriceErr(false);
    setAdditionalRatesPerMinsErr(false);

    let ok = true;
    const nameTrim = (addOnName ?? "").trim();
    const typeTrim = (addOnType ?? "").trim();
    const textMax = validationLength.departmentName;
    const longTextMax = validationLength.reservationGuestNotesText;

    if (!nameTrim) {
      setNameError(true);
      ok = false;
    } else if (!validateString(nameTrim, textMax, validationType.SPACE)) {
      setNameError(true);
      ok = false;
    }
    if (!typeTrim) {
      setAddOnTypeErr(true);
      ok = false;
    } else if (!validateString(typeTrim, textMax, validationType.SPACE)) {
      setAddOnTypeErr(true);
      ok = false;
    }

    const optionalSpace = (
      val: string | null,
      setErr: (v: boolean) => void,
    ) => {
      const t = (val ?? "").trim();
      if (t && !validateString(t, textMax, validationType.SPACE)) {
        setErr(true);
        ok = false;
      }
    };
    const optionalName = (
      val: string | null,
      max: number,
      setErr: (v: boolean) => void,
    ) => {
      const t = (val ?? "").trim();
      if (t && !validateString(t, max, validationType.NAME)) {
        setErr(true);
        ok = false;
      }
    };
    const optionalDigits = (
      val: string | null,
      setErr: (v: boolean) => void,
    ) => {
      const t = (val ?? "").trim();
      if (t && !validateString(t, textMax, validationType.OTHER)) {
        setErr(true);
        ok = false;
      }
    };

    optionalName(addOnDescription, longTextMax, setAddOnDescriptionErr);
    optionalSpace(addOnCategory, setAddOnCategoryErr);
    optionalSpace(addOnSubCategory, setAddOnSubCategoryErr);
    optionalDigits(qtyInStock, setQtyInStockErr);
    optionalName(price, textMax, setPriceErr);
    optionalDigits(additionalRatesPerMins, setAdditionalRatesPerMinsErr);

    if (!ok) {
      if (!nameTrim) {
        setAlertMessage(
          `${RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME} is mandatory`,
        );
      } else if (!typeTrim) {
        setAlertMessage(
          `${RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_TYPE} is mandatory`,
        );
      } else {
        setAlertMessage("Please correct the highlighted fields");
      }
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    setAddOnInformation(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(addOnInformation);
    editClicked &&
      setSelectedId(
        checkData(addOnInformation, selectedId) ? selectedId : null,
      );
  }, [addOnInformation]);

  const updateAddOn = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (addOnInformation ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME,
        addOnName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setAddOnInformation((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (String(item.id) === String(selectedId)) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editAddOn(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${ReservePropertyEnum.equipmentsAddOns} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addAddOn = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        addOnInformation ?? [],
        RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME,
        addOnName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setAddOnInformation((prev: any) => {
      return updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editAddOn(null);
    setEditClicked(false);
  };

  const editAddOn = (list: any) => {
    if (list == null) {
      setAddOnName(null);
      setAddOnType(null);
      setAddOnDescription(null);
      setAddOnCategory(null);
      setAddOnSubCategory(null);
      setQtyInStock(null);
      setPricingType(null);
      setPrice(null);
      setAdditionalRatesPerMins(null);
      setNameError(false);
      setAddOnTypeErr(false);
      setAddOnDescriptionErr(false);
      setAddOnCategoryErr(false);
      setAddOnSubCategoryErr(false);
      setQtyInStockErr(false);
      setPriceErr(false);
      setAdditionalRatesPerMinsErr(false);
      setEditClicked(false);
      return;
    }
    if (list?.id != null && list.id !== "") {
      setSelectedId(list.id);
    } else {
      setSelectedId(null);
    }
    setEditClicked(true);
    const nameFromList =
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME] ??
      list?.[RESERVE_EQUIPMENTS_ADDON_LIST_CODE_KEY];
    const typeFromList =
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_TYPE] ??
      list?.[RESERVE_EQUIPMENTS_ADDON_LIST_NAME_KEY];
    setAddOnName(
      nameFromList != null && nameFromList !== "" ? String(nameFromList) : null,
    );
    setAddOnType(
      typeFromList != null && typeFromList !== "" ? String(typeFromList) : null,
    );
    setAddOnDescription(
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION] != null &&
        list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION] !== ""
        ? String(list[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION])
        : null,
    );
    setAddOnCategory(
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY] != null &&
        list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY] !== ""
        ? String(list[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY])
        : null,
    );
    setAddOnSubCategory(
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY] != null &&
        list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY] !== ""
        ? String(list[RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY])
        : null,
    );
    setQtyInStock(
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK] != null &&
        list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK] !== ""
        ? String(list[RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK])
        : null,
    );
    const pricingTypeRaw = list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_PRICING_TYPE];
    if (pricingTypeRaw != null && pricingTypeRaw !== "") {
      const normalized = String(pricingTypeRaw).trim().toLowerCase();
      if (normalized === "by duration") {
        setPricingType("By duration");
      } else if (normalized === "by session") {
        setPricingType("By session");
      } else {
        setPricingType(String(pricingTypeRaw));
      }
    } else {
      setPricingType(null);
    }
    setPrice(
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE] != null &&
        list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE] !== ""
        ? String(list[RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE])
        : null,
    );
    setAdditionalRatesPerMins(
      list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS] !=
        null &&
        list?.[RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS] !== ""
        ? String(list[RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS])
        : null,
    );
    setNameError(false);
    setAddOnTypeErr(false);
    setAddOnDescriptionErr(false);
    setAddOnCategoryErr(false);
    setAddOnSubCategoryErr(false);
    setQtyInStockErr(false);
    setPriceErr(false);
    setAdditionalRatesPerMinsErr(false);
  };

  const deleteAddOn = (id: number) => {
    setAddOnInformation(deleteItem(addOnInformation ?? [], id));
  };

  const addOnNameHelperText = nameError
    ? !addOnName?.trim()
      ? reserveEquipmentsAddonsFieldErrors.required
      : reserveEquipmentsAddonsFieldErrors.addOnName
    : reserveEquipmentsAddonsFieldHelperText.addOnName;

  const addOnTypeHelperText = addOnTypeErr
    ? !addOnType?.trim()
      ? reserveEquipmentsAddonsFieldErrors.required
      : reserveEquipmentsAddonsFieldErrors.addOnType
    : reserveEquipmentsAddonsFieldHelperText.addOnType;

  const addOnDescriptionHelperText = addOnDescriptionErr
    ? reserveEquipmentsAddonsFieldErrors.addOnDescription
    : reserveEquipmentsAddonsFieldHelperText.addOnDescription;

  const addOnCategoryHelperText = addOnCategoryErr
    ? reserveEquipmentsAddonsFieldErrors.addOnCategory
    : reserveEquipmentsAddonsFieldHelperText.addOnCategory;

  const addOnSubCategoryHelperText = addOnSubCategoryErr
    ? reserveEquipmentsAddonsFieldErrors.addOnSubCategory
    : reserveEquipmentsAddonsFieldHelperText.addOnSubCategory;

  const qtyInStockHelperText = qtyInStockErr
    ? reserveEquipmentsAddonsFieldErrors.qtyInStock
    : reserveEquipmentsAddonsFieldHelperText.qtyInStock;

  const priceHelperText = priceErr
    ? reserveEquipmentsAddonsFieldErrors.price
    : reserveEquipmentsAddonsFieldHelperText.price;

  const additionalRatesPerMinsHelperText = additionalRatesPerMinsErr
    ? reserveEquipmentsAddonsFieldErrors.additionalRatesPerMins
    : reserveEquipmentsAddonsFieldHelperText.additionalRatesPerMins;

  const payload = {
    [RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME]: addOnName,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_TYPE]: addOnType,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION]: addOnDescription,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY]: addOnCategory,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY]: addOnSubCategory,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK]: qtyInStock,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_PRICING_TYPE]: pricingType,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE]: price,
    [RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS]:
      additionalRatesPerMins,
    [RESERVE_EQUIPMENTS_ADDON_LIST_CODE_KEY]: (addOnName ?? "").trim(),
    [RESERVE_EQUIPMENTS_ADDON_LIST_NAME_KEY]: (addOnType ?? "").trim(),
  };

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            {ReservePropertyEnum.equipmentsAddOns}
            <Tooltip
              title={tooltipComponent(reserveEquipmentsAddonsTooltipHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editAddOn(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editAddOn(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME}
            <Tooltip
              title={tooltipComponent(reserveEquipmentsAddonsTooltipAddOnName)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-name"
            variant="outlined"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_NAME}
            value={addOnName ?? ""}
            helperText={addOnNameHelperText}
            error={nameError}
            onChange={(e: any) => {
              setAddOnName(e.target.value);
              setNameError(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_TYPE}
            <Tooltip
              title={tooltipComponent(reserveEquipmentsAddonsTooltipAddOnType)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-type"
            variant="outlined"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_TYPE}
            value={addOnType ?? ""}
            helperText={addOnTypeHelperText}
            error={addOnTypeErr}
            onChange={(e: any) => {
              setAddOnType(e.target.value);
              setAddOnTypeErr(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION}
            <Tooltip
              title={tooltipComponent(
                reserveEquipmentsAddonsTooltipAddOnDescription,
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-description"
            variant="outlined"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_DESCRIPTION}
            value={addOnDescription ?? ""}
            multiline
            minRows={2}
            error={addOnDescriptionErr}
            helperText={addOnDescriptionHelperText}
            onChange={(e: any) => {
              setAddOnDescription(e.target.value);
              setAddOnDescriptionErr(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY}
            <Tooltip
              title={tooltipComponent(
                reserveEquipmentsAddonsTooltipAddOnCategory,
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-category"
            variant="outlined"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_CATEGORY}
            value={addOnCategory ?? ""}
            error={addOnCategoryErr}
            helperText={addOnCategoryHelperText}
            onChange={(e: any) => {
              setAddOnCategory(e.target.value);
              setAddOnCategoryErr(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY}
            <Tooltip
              title={tooltipComponent(
                reserveEquipmentsAddonsTooltipAddOnSubCategory,
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-subcategory"
            variant="outlined"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_ADD_ON_SUB_CATEGORY}
            value={addOnSubCategory ?? ""}
            error={addOnSubCategoryErr}
            helperText={addOnSubCategoryHelperText}
            onChange={(e: any) => {
              setAddOnSubCategory(e.target.value);
              setAddOnSubCategoryErr(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK}
            <Tooltip
              title={tooltipComponent(reserveEquipmentsAddonsTooltipQtyInStock)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-qty"
            variant="outlined"
            type="number"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_QTY_IN_STOCK}
            value={qtyInStock ?? ""}
            error={qtyInStockErr}
            helperText={qtyInStockHelperText}
            onChange={(e: any) => {
              setQtyInStock(e.target.value);
              setQtyInStockErr(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_PRICING_TYPE}
            <Tooltip
              title={tooltipComponent(
                reserveEquipmentsAddonsTooltipPricingType,
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <FormControl fullWidth size="small" variant="outlined">
            <Select
              id="reserve-equipment-addon-pricing-type"
              displayEmpty
              value={pricingType ?? ""}
              onChange={(e: SelectChangeEvent<string>) =>
                setPricingType(e.target.value || null)
              }
              renderValue={(selected) =>
                selected === "" ? (
                  <Typography variant="body2" color="text.secondary">
                    Select pricing type
                  </Typography>
                ) : (
                  selected
                )
              }
            >
              <MenuItem value="">
                <em>Select pricing type</em>
              </MenuItem>
              {RESERVE_EQUIPMENTS_ADDON_PRICING_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE}
            <Tooltip
              title={tooltipComponent(reserveEquipmentsAddonsTooltipPrice)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-price"
            type="number"
            variant="outlined"
            placeholder={RESERVE_EQUIPMENTS_ADDON_FIELD_PRICE}
            value={price ?? ""}
            error={priceErr}
            helperText={priceHelperText}
            onChange={(e: any) => {
              setPrice(e.target.value);
              setPriceErr(false);
            }}
          />
        </Stack>

        <Stack>
          <FormLabel component="legend">
            {RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS}
            <Tooltip
              title={tooltipComponent(
                reserveEquipmentsAddonsTooltipAdditionalRatesPerMins,
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserve-equipment-addon-additional-rates"
            variant="outlined"
            type="number"
            placeholder={
              RESERVE_EQUIPMENTS_ADDON_FIELD_ADDITIONAL_RATES_PER_MINS
            }
            value={additionalRatesPerMins ?? ""}
            error={additionalRatesPerMinsErr}
            helperText={additionalRatesPerMinsHelperText}
            onChange={(e: any) => {
              setAdditionalRatesPerMins(e.target.value);
              setAdditionalRatesPerMinsErr(false);
            }}
          />
        </Stack>

        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateAddOn() : addAddOn())}
            id="addReserveEquipmentAddon"
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
            {ReservePropertyEnum.equipmentsAddOns}
          </Button>
        </Stack>

        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography
            variant="h6"
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {ReservePropertyEnum.equipmentsAddOns} available
            <Tooltip
              title={tooltipComponent(reserveListAvailableHeadingTooltip)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="column" gap="10px">
            {Array.isArray(addOnInformation) && addOnInformation.length > 0 ? (
              <DraggableItemList
                itemData={addOnInformation}
                onOrderUpdate={(order) => setAddOnInformation(order)}
                onEdit={(info) => editAddOn(info)}
                onDelete={(id) => deleteAddOn(id)}
                code={RESERVE_EQUIPMENTS_ADDON_LIST_CODE_KEY}
                name={RESERVE_EQUIPMENTS_ADDON_LIST_NAME_KEY}
              />
            ) : (
              <Typography variant="body2">
                No {ReservePropertyEnum.equipmentsAddOns} information available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveEquipmentsAddOns;
